import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { extend, injectStore, NgtArgs } from 'angular-three';
import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { DicePhysicsService } from '../../services/dice-physics.service';
import {
  createDiceMesh,
  DiceGeometryParams,
} from '../../utils/dice-geometry.util';
import {
  DiceRollerConfig,
  DiceRollResult,
  ThrowParameters,
  PhysicsFrame,
  DieMaterial,
  SurfaceMaterial,
} from '../../models/dice-roller.types';
import { DEFAULT_DICE_CONFIG } from '../../models/dice-faces.const';

// Extend Three.js classes for Angular Three
extend({
  Mesh: THREE.Mesh,
  Group: THREE.Group,
  BoxGeometry: THREE.BoxGeometry,
  PlaneGeometry: THREE.PlaneGeometry,
  MeshStandardMaterial: THREE.MeshStandardMaterial,
  MeshBasicMaterial: THREE.MeshBasicMaterial,
  AmbientLight: THREE.AmbientLight,
  DirectionalLight: THREE.DirectionalLight,
  ShadowMaterial: THREE.ShadowMaterial,
});

/**
 * Dice Roller Component
 *
 * Angular Three component for 3D dice with record-and-replay physics.
 * Achieves 100% visual accuracy by replaying exact simulation frames.
 *
 * **Features:**
 * - 100% accuracy: Simulation records frames, component replays them
 * - Predetermined mode: Guaranteed to land on target value
 * - Random mode: Generates random outcomes
 * - Scene rotation awareness: Adapts for flat/vertical AR scenes
 *
 * **Usage:**
 * ```html
 * <lib-dice-roller 
 *   [config]="diceConfig" 
 *   (rollComplete)="onRollComplete($event)" 
 * />
 * ```
 *
 * **API (via scene.userData):**
 * ```typescript
 * const diceRoller = scene.userData['diceRoller'];
 * await diceRoller.roll(targetValue); // Predetermined
 * await diceRoller.rollRandom();      // Random
 * ```
 */
@Component({
  selector: 'lib-dice-roller',
  standalone: true,
  imports: [NgtArgs],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <!-- Dice Meshes (playback-driven, positions updated from recorded frames) -->
    @for (diceGeom of diceGeometries(); track $index) {
      <ngt-primitive
        *args="[diceGeom]"
        [parameters]="{
          position: dicePositions()[$index] || [0, 0, 1.5],
          rotation: diceRotations()[$index] || [0, 0, 0]
        }"
      />
    }
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
  `],
})
export class UiDiceRollerComponent {
  private store = injectStore();
  private physics = inject(DicePhysicsService);

  // Inputs
  config = input<DiceRollerConfig>(DEFAULT_DICE_CONFIG);
  enableCameraControls = input(false);

  // Outputs
  rollStart = output<void>();
  rollComplete = output<DiceRollResult>();

  // State (supports multiple dice)
  private diceBodies = signal<CANNON.Body[]>([]);
  private diceMeshes = signal<THREE.Group[]>([]);
  private isRolling = signal(false);
  
  // Position and rotation for template binding (array per die)
  protected dicePositions = signal<[number, number, number][]>([]);
  protected diceRotations = signal<[number, number, number][]>([]);

  // Computed geometries (array of dice meshes)
  protected diceGeometries = computed(() => {
    const cfg = this.config();
    const diceCount = cfg.diceCount || 1;
    const params: DiceGeometryParams = {
      size: cfg.size || 0.6,
      segments: cfg.segments || 40,
      edgeRadius: cfg.edgeRadius || 0.08,
      notchRadius: cfg.notchRadius || 0.15,
      notchDepth: cfg.notchDepth || 0.17,
    };
    
    // Create array of dice meshes
    const meshes: THREE.Group[] = [];
    const scale = cfg.scale || 1.0;
    const spacing = (cfg.size || 0.6) * scale * 2; // 2x die size separation
    
    for (let i = 0; i < diceCount; i++) {
      const diceMesh = createDiceMesh(params);
      diceMesh.scale.set(scale, scale, scale);
      
      // Position each die with horizontal spacing
      const offsetX = (i - (diceCount - 1) / 2) * spacing;
      diceMesh.position.set(offsetX, 0, 1.5);
      
      meshes.push(diceMesh);
    }
    
    return meshes;
  });

  // Scene rotation for group
  protected sceneGroupRotation = computed(() => {
    const rotation = this.config().sceneRotation || [0, 0, 0];
    return rotation as [number, number, number];
  });

  // Floor shadow material
  protected floorMaterial = computed(
    () =>
      new THREE.ShadowMaterial({
        opacity: 0.3,
      })
  );

  protected readonly Math = Math;

  constructor() {
    // Store reference to dice meshes when geometries change
    effect(() => {
      const meshes = this.diceGeometries();
      if (meshes.length > 0) {
        this.diceMeshes.set(meshes);
        
        // Initialize positions
        const positions: [number, number, number][] = meshes.map(mesh => [
          mesh.position.x,
          mesh.position.y,
          mesh.position.z
        ]);
        this.dicePositions.set(positions);
        
        // Initialize rotations
        const rotations: [number, number, number][] = meshes.map(() => [0, 0, 0]);
        this.diceRotations.set(rotations);
      }
    });

    // Expose API via scene.userData
    effect(() => {
      const scene = this.store.get().scene;
      if (scene) {
        scene.userData['diceRoller'] = {
          roll: this.roll.bind(this),
          isRolling: () => this.isRolling(),
          getState: () => this.physics.rollState(),
        };
      }
    });

    // Create dice bodies for positioning (playback updates these directly)
    // Note: Physics simulation happens in DicePhysicsService.simulationWorld
    effect(() => {
      const cfg = this.config();
      const diceCount = cfg.diceCount || 1;
      const dieSize = cfg.size || 0.6;
      const scale = cfg.scale || 1.0;
      const spacing = dieSize * scale * 2;

      // Create bodies for each die
      const bodies: CANNON.Body[] = [];
      for (let i = 0; i < diceCount; i++) {
        // Use configured start position or generate default
        const startPos = cfg.startPosition 
          ? [...cfg.startPosition] as [number, number, number]
          : [0, 0, 1.5];
        
        // Add horizontal offset for multiple dice
        const offsetX = (i - (diceCount - 1) / 2) * spacing;
        startPos[0] += offsetX;
        
        const body = new CANNON.Body({
          mass: 1,
          shape: new CANNON.Box(
            new CANNON.Vec3(dieSize / 2, dieSize / 2, dieSize / 2)
          ),
          position: new CANNON.Vec3(...startPos),
          allowSleep: false
        });
        
        bodies.push(body);
      }
      
      this.diceBodies.set(bodies);
      
      // Update initial visual positions
      const positions = bodies.map(body => [
        body.position.x,
        body.position.y,
        body.position.z
      ] as [number, number, number]);
      this.dicePositions.set(positions);

      // Cleanup on destroy
      return () => {
        this.diceBodies.set([]);
      };
    });

    // Sync dice mesh positions/rotations with physics bodies
    // NOTE: Animation now handled by playbackRecordedFrames() for 100% accuracy
    // This effect is kept for any non-roll physics updates
    effect(() => {
      const bodies = this.diceBodies();
      const rolling = this.isRolling();

      if (bodies.length === 0 || rolling) {
        return; // Skip if no bodies or if rolling (playback handles animation)
      }

      // Only update positions when NOT rolling
      const positions = bodies.map(body => [
        body.position.x,
        body.position.y,
        body.position.z
      ] as [number, number, number]);
      this.dicePositions.set(positions);
      
      const rotations = bodies.map(body => {
        const euler = new THREE.Euler().setFromQuaternion(
          new THREE.Quaternion(body.quaternion.x, body.quaternion.y, body.quaternion.z, body.quaternion.w)
        );
        return [euler.x, euler.y, euler.z] as [number, number, number];
      });
      this.diceRotations.set(rotations);
    });
  }

  /**
   * Resets dice state. Call when restarting game.
   */
  reset(): void {
    // Reset physics service
    this.physics.reset();
    
    // Reset all dice bodies to initial state
    const bodies = this.diceBodies();
    const cfg = this.config();
    const diceCount = cfg.diceCount || 1;
    const dieSize = cfg.size || 0.6;
    const scale = cfg.scale || 1.0;
    const spacing = dieSize * scale * 2;
    
    bodies.forEach((body, i) => {
      const startPos = cfg.startPosition 
        ? [...cfg.startPosition] as [number, number, number]
        : [0, 0, 1.5];
      
      const offsetX = (i - (diceCount - 1) / 2) * spacing;
      startPos[0] += offsetX;
      
      body.position.set(startPos[0], startPos[1], startPos[2]);
      body.quaternion.set(0, 0, 0, 1); // Identity quaternion
      body.velocity.setZero();
      body.angularVelocity.setZero();
      body.force.setZero();
      body.torque.setZero();
    });
    
    // Update visual positions
    const positions = bodies.map(body => [
      body.position.x,
      body.position.y,
      body.position.z
    ] as [number, number, number]);
    this.dicePositions.set(positions);
    
    const rotations = bodies.map(() => [0, 0, 0] as [number, number, number]);
    this.diceRotations.set(rotations);
    
    // Reset rolling state
    this.isRolling.set(false);
  }

  /**
   * Plays back recorded physics frames for 100% accurate animation of multiple dice.
   * Also triggers collision audio based on recorded collision events.
   * 
   * This guarantees visual accuracy by replaying exact simulation frames
   * instead of re-simulating physics (which causes divergence).
   * 
   * @param allFrames Array of frame arrays (one per die)
   * @returns Promise resolving when playback completes
   */
  private playbackMultiDiceFrames(allFrames: PhysicsFrame[][]): Promise<void> {
    return new Promise((resolve) => {
      let frameIndex = 0;
      const bodies = this.diceBodies();
      const config = this.config();
      
      if (bodies.length === 0 || allFrames.length === 0) {
        resolve();
        return;
      }
      
      // Find max frame count across all dice
      const maxFrames = Math.max(...allFrames.map(frames => frames.length));
      
      const playFrame = () => {
        if (frameIndex >= maxFrames) {
          // Playback complete
          this.isRolling.set(false);
          this.physics['rollState'].set('idle');
          resolve();
          return;
        }
        
        // Apply current frame to all dice
        const positions: [number, number, number][] = [];
        const rotations: [number, number, number][] = [];
        
        bodies.forEach((body, i) => {
          const frames = allFrames[i];
          
          // Use last frame if this die's animation is shorter
          const frameIdx = Math.min(frameIndex, frames.length - 1);
          const frame = frames[frameIdx];
          
          // Apply to body
          body.position.set(...frame.position);
          body.quaternion.set(...frame.quaternion);
          
          // Store for signal update
          positions.push(frame.position);
          
          const euler = new THREE.Euler().setFromQuaternion(
            new THREE.Quaternion(...frame.quaternion)
          );
          rotations.push([euler.x, euler.y, euler.z]);
          
          // Play collision audio if present in this frame
          if (frame.collision && !config.mute) {
            this.playCollisionSound(
              frame.collision.type,
              frame.collision.intensity,
              config.dieMaterial || 'plastic',
              config.surfaceMaterial || 'felt'
            );
          }
        });
        
        // Update signals for all dice at once
        this.dicePositions.set(positions);
        this.diceRotations.set(rotations);
        
        frameIndex++;
        requestAnimationFrame(playFrame);
      };
      
      playFrame();
    });
  }
  
  /**
   * Plays collision sound based on type and materials.
   * 
   * Sound files are selected using the following convention:
   * - Die-to-die: `/assets/sfx/dicehit/dicehit_[material][1-3].mp3`
   * - Die-to-surface: `/assets/sfx/surface/surface_[material][1-3].mp3`
   * 
   * Volume is scaled by collision intensity (0.2-0.8 range) for realistic audio.
   * Random variation (1-3) is selected to prevent repetitive sounds.
   * 
   * @param type Collision type (die-to-die or die-to-surface)
   * @param intensity Impact intensity normalized to 0-1
   * @param dieMaterial Die material for sound selection
   * @param surfaceMaterial Surface material for sound selection
   */
  private playCollisionSound(
    type: 'die-to-die' | 'die-to-surface',
    intensity: number,
    dieMaterial: DieMaterial,
    surfaceMaterial: SurfaceMaterial
  ): void {
    // Determine sound file path based on collision type
    const soundFolder = type === 'die-to-die' ? 'dicehit' : 'surface';
    const material = type === 'die-to-die' ? dieMaterial : surfaceMaterial;
    
    // Each material has 3 variations (1-3)
    const variation = Math.floor(Math.random() * 3) + 1;
    const soundPath = `/assets/sfx/${soundFolder}/${soundFolder}_${material}${variation}.mp3`;
    
    // Create and play audio
    const audio = new Audio(soundPath);
    audio.volume = Math.max(0.2, Math.min(0.8, intensity)); // Volume based on intensity (0.2-0.8 range)
    
    // Play sound (errors are expected if files are missing or browser blocks autoplay)
    audio.play().catch(() => {
      // Silently ignore audio errors to prevent breaking animation
      // Common causes: missing audio files, browser autoplay restrictions
    });
  }

  /**
   * Rolls the die/dice to specific target value(s).
   * 
   * @param targetValues Target face value (1-6) or array of values for multiple dice
   * @returns Promise resolving to roll result
   */
  async roll(targetValues: number | number[]): Promise<DiceRollResult> {
    if (this.isRolling()) {
      throw new Error('Roll already in progress');
    }

    const bodies = this.diceBodies();
    if (bodies.length === 0) {
      throw new Error('Dice bodies not initialized');
    }

    const diceCount = this.config().diceCount;

    // For multi-dice: accept either a single target total OR an array of specific values
    // For single die: must be a single number
    const isSingleValue = typeof targetValues === 'number';
    
    // Validate input based on dice count
    if (diceCount === 1) {
      if (!isSingleValue) {
        throw new Error(`Single die roll expects a number, received array`);
      }
    } else {
      // Multi-dice: Allow single number (target total) or array (specific values per die)
      if (!isSingleValue && Array.isArray(targetValues) && targetValues.length !== diceCount) {
        throw new Error(`Expected ${diceCount} target value(s), received ${targetValues.length}`);
      }
    }

    this.isRolling.set(true);
    this.rollStart.emit();

    try {
      if (diceCount === 1) {
        // Single die - use existing method
        const targetValue = isSingleValue ? targetValues as number : (targetValues as number[])[0];
        const params = await this.physics.findValidThrow(
          targetValue,
          this.config()
        );

        // Set initial position and rotation
        const body = bodies[0];
        body.position.set(...params.startPos);
        body.quaternion.set(
          params.startRotation.x,
          params.startRotation.y,
          params.startRotation.z,
          params.startRotation.w
        );
        
        // Playback single die
        await this.playbackMultiDiceFrames([params.recordedFrames]);

        const result: DiceRollResult = {
          values: [targetValue],
          duration: 0,
          attemptCount: 1,
          success: true,
        };

        this.rollComplete.emit(result);
        return result;
      } else {
        // Multi-dice - accept either target total (number) or specific values (array)
        const targetTotal = isSingleValue 
          ? targetValues as number 
          : (targetValues as number[]).reduce((sum, val) => sum + val, 0);
        
        const allParams = await this.physics.findValidMultiDiceThrow(
          targetTotal,
          diceCount,
          this.config()
        );

        // Set initial positions and rotations for all dice
        bodies.forEach((body, i) => {
          const params = allParams[i];
          body.position.set(...params.startPos);
          body.quaternion.set(
            params.startRotation.x,
            params.startRotation.y,
            params.startRotation.z,
            params.startRotation.w
          );
        });
        
        // Playback all dice synchronized
        const allFrames = allParams.map(params => params.recordedFrames);
        await this.playbackMultiDiceFrames(allFrames);

        // Detect actual landed values from final frame positions
        const actualValues = bodies.map(body => {
          const result = this.physics.detectLandedFace(body, this.config().sceneRotation || [0, 0, 0]);
          return result.value;
        });

        const result: DiceRollResult = {
          values: actualValues, // Return ACTUAL values from simulation
          duration: 0,
          attemptCount: 1,
          success: true,
        };

        this.rollComplete.emit(result);
        return result;
      }
    } finally {
      this.isRolling.set(false);
    }
  }
}
